import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import inventoryService from '../../services/inventoryService';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import { AuthContext } from '../../context/AuthContext';
import printerService from '../../services/printerService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const money = (value, decimals = 2) => `$${(parseFloat(value) || 0).toFixed(decimals)}`;
// Costo unitario con hasta 4 decimales (insumos por gramo / mililitro), sin ceros sobrantes
const unitCost = (value) => {
    const n = parseFloat(value) || 0;
    return `$${n >= 1 ? n.toFixed(2) : n.toFixed(4).replace(/0{1,2}$/, '')}`;
};
const formatQty = (value) => {
    const n = parseFloat(value) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, '');
};

const Bodega = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

    const [activeTab, setActiveTab] = useState('insumos'); // insumos, movimientos, recetas
    
    // Estados generales
    const [supplies, setSupplies] = useState([]);
    const [movements, setMovements] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estado para Modales
    const [isSupplyModalOpen, setIsSupplyModalOpen] = useState(false);
    const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
    const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
    const [printModal, setPrintModal] = useState({ isOpen: false, success: true, message: '' });
    
    // Formularios
    const [editingSupply, setEditingSupply] = useState(null);
    const [supplyForm, setSupplyForm] = useState({ name: '', description: '', unit: '', current_stock: 0, cost_per_unit: '', is_active: true, is_production_item: false });
    const [unitOptions, setUnitOptions] = useState([]);
    
    const [movementForm, setMovementForm] = useState({ supply: '', movement_type: 'in', quantity: 0, unit_cost: '', total_cost: '', reason: '', lockedSupply: false });
    
    const [allRecipes, setAllRecipes] = useState([]);
    const [recipeForm, setRecipeForm] = useState({ id: null, product: '', supply: '', quantity: 0 });
    const [viewDetailsProductId, setViewDetailsProductId] = useState(null);

    // (Mezclas movido a su propia página /fast-food/mezclas)

    const fetchData = async () => {
        setLoading(true);
        try {
            const [suppliesData, productsData, optionsData] = await Promise.all([
                inventoryService.getSupplies(),
                api.get('/api/menu/products/', { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }),
                api.options('/api/menu/supplies/', { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE })
            ]);
            setSupplies(suppliesData);
            setProducts(productsData.data?.results || productsData.data || []);
            
            if (optionsData.data?.actions?.POST?.unit?.choices) {
                setUnitOptions(optionsData.data.actions.POST.unit.choices);
                if (optionsData.data.actions.POST.unit.choices.length > 0 && !supplyForm.unit) {
                    setSupplyForm(prev => ({ ...prev, unit: optionsData.data.actions.POST.unit.choices[0].value }));
                }
            }
            
            if (activeTab === 'movimientos') {
                const movs = await inventoryService.getMovements();
                setMovements(movs);
            }
            
            if (activeTab === 'recetas') {
                const recs = await inventoryService.getRecipeIngredients();
                setAllRecipes(recs);
            }
        } catch (error) {
            console.error("Error fetching bodega data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    // ---- HANDLERS INSUMOS ----
    const handleSupplySubmit = async (e) => {
        e.preventDefault();
        try {
            const costPerUnit = parseFloat(supplyForm.cost_per_unit);
            if (editingSupply) {
                await inventoryService.updateSupply(editingSupply.id, {
                    ...supplyForm,
                    cost_per_unit: isNaN(costPerUnit) ? 0 : costPerUnit
                });
            } else {
                const stockToSet = parseFloat(supplyForm.current_stock) || 0;
                const newSupply = await inventoryService.createSupply({ ...supplyForm, current_stock: 0, cost_per_unit: 0 }); // create with 0
                if (stockToSet > 0) {
                    // El stock inicial queda registrado como compra con su precio
                    await inventoryService.recordMovement(newSupply.id, {
                        movement_type: 'in',
                        quantity: stockToSet,
                        unit_cost: isNaN(costPerUnit) ? '' : costPerUnit,
                        reason: 'Stock inicial'
                    });
                } else if (!isNaN(costPerUnit) && costPerUnit > 0) {
                    await inventoryService.updateSupply(newSupply.id, { cost_per_unit: costPerUnit });
                }
            }
            setIsSupplyModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al guardar insumo');
        }
    };

    const handleDeleteSupply = async (id) => {
        if(window.confirm('¿Eliminar este insumo? Se eliminarán también sus movimientos.')) {
            try {
                await inventoryService.deleteSupply(id);
                fetchData();
            } catch (error) {
                alert('Error al eliminar insumo');
            }
        }
    };

    // ---- HANDLERS MOVIMIENTOS ----
    const handleMovementSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                movement_type: movementForm.movement_type,
                quantity: parseFloat(movementForm.quantity),
                reason: movementForm.reason
            };
            if (movementForm.movement_type === 'in') {
                if (movementForm.unit_cost !== '') payload.unit_cost = parseFloat(movementForm.unit_cost);
                else if (movementForm.total_cost !== '') payload.total_cost = parseFloat(movementForm.total_cost);
            }
            await inventoryService.recordMovement(movementForm.supply, payload);
            setIsMovementModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al registrar movimiento. Verifique los datos.');
        }
    };

    // ---- HANDLERS RECETAS ----
    const loadAllRecipes = async () => {
        try {
            const data = await inventoryService.getRecipeIngredients();
            setAllRecipes(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRecipeSubmit = async (e) => {
        e.preventDefault();
        try {
            // Edit not implemented in service, but we can delete and create if needed, 
            // or if the service supports it we would use updateRecipeIngredient.
            // But wait, the service only has createRecipeIngredient and deleteRecipeIngredient.
            // So if editing, we delete the old and create the new.
            if (recipeForm.id) {
                await inventoryService.deleteRecipeIngredient(recipeForm.id);
            }
            await inventoryService.createRecipeIngredient({
                product: recipeForm.product,
                supply: recipeForm.supply,
                quantity: parseFloat(recipeForm.quantity),
                size: null
            });
            setIsRecipeModalOpen(false);
            loadAllRecipes();
        } catch (error) {
            console.error(error.response?.data || error);
            const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            alert('Error al guardar receta: ' + msg);
        }
    };
    
    const handleDeleteRecipe = async (id) => {
        if(window.confirm('¿Eliminar esta receta?')) {
            try {
                await inventoryService.deleteRecipeIngredient(id);
                loadAllRecipes();
            } catch (error) {
                alert('Error al eliminar');
            }
        }
    }


    const recipeCost = (recipes) => recipes.reduce((acc, r) => {
        const supply = supplies.find(s => s.id === r.supply);
        return acc + (parseFloat(r.quantity) || 0) * (parseFloat(supply?.cost_per_unit) || 0);
    }, 0);

    const getStockDisplay = (supplyId) => {
        const s = supplies.find(sp => sp.id === supplyId);
        if (!s) return { stock: 0, unit: '', color: 'gray' };
        return {
            stock: s.current_stock,
            unit: s.unit_display,
            color: s.current_stock <= 0 ? 'red' : 'green'
        };
    };

    const handlePrintThermalStock = async () => {
        const chars_per_line = 48;
        let lines = [];

        const center = (text) => {
            const padding = Math.max(0, Math.floor((chars_per_line - text.length) / 2));
            return ' '.repeat(padding) + text;
        };

        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };

        const current_time = new Date();
        const pad0 = (n) => n.toString().padStart(2, '0');
        const dateStr = `${current_time.getFullYear()}-${pad0(current_time.getMonth()+1)}-${pad0(current_time.getDate())} ${pad0(current_time.getHours())}:${pad0(current_time.getMinutes())}:${pad0(current_time.getSeconds())}`;
        const userName = user?.username || user?.email || 'Usuario';

        lines.push(center("REPORTE DE BODEGA"));
        lines.push(center("INVENTARIO DE INSUMOS"));
        lines.push(center(dateStr));
        lines.push(center(`USUARIO: ${userName.toUpperCase()}`));
        lines.push("");

        lines.push("-".repeat(chars_per_line));
        // Encabezado de columnas: 26 chars nombre + 10 chars stock + 12 chars unidad = 48 chars
        const colName = "INSUMO".padEnd(26, ' ');
        const colStock = "STOCK".padStart(10, ' ');
        const colUnit = "UNIDAD".padStart(12, ' ');
        lines.push(`${colName}${colStock}${colUnit}`);
        lines.push("-".repeat(chars_per_line));

        let activeCount = 0;
        let lowStockCount = 0;

        supplies.forEach(s => {
            if (s.is_active) activeCount++;
            if (parseFloat(s.current_stock) <= 0) lowStockCount++;

            const stockVal = parseFloat(s.current_stock || 0);
            const stockDisplay = Number.isInteger(stockVal) ? String(stockVal) : stockVal.toFixed(2);
            
            const rawName = s.name || '';
            const nameTruncated = rawName.length > 25 ? rawName.substring(0, 25) : rawName;
            const namePad = nameTruncated.padEnd(26, ' ');
            const stockPad = stockDisplay.padStart(10, ' ');
            const unitStr = (s.unit_display || s.unit || '').substring(0, 11);
            const unitPad = unitStr.padStart(12, ' ');

            lines.push(`${namePad}${stockPad}${unitPad}`);
        });

        lines.push("-".repeat(chars_per_line));
        lines.push(center("RESUMEN DE BODEGA"));
        lines.push("-".repeat(chars_per_line));
        lines.push(rightAlign("TOTAL INSUMOS", supplies.length));
        lines.push(rightAlign("INSUMOS ACTIVOS", activeCount));
        lines.push(rightAlign("INSUMOS AGOTADOS / EN CERO", lowStockCount));
        lines.push("-".repeat(chars_per_line));
        lines.push("");
        lines.push("");

        const ticketContent = lines.join("\n");

        try {
            await printerService.printCustomTicket(ticketContent, 'report');
            setPrintModal({
                isOpen: true,
                success: true,
                message: 'El ticket de inventario se ha enviado correctamente a la impresora.'
            });
        } catch (error) {
            console.error("Error al imprimir reporte térmico de bodega:", error);
            const errDetail = error.response?.data?.error || error.message || 'Error desconocido';
            setPrintModal({
                isOpen: true,
                success: false,
                message: `No se pudo imprimir el ticket: ${errDetail}. Verifique la conexión con la impresora.`
            });
        }
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        doc.text('Reporte de Bodega e Insumos', 14, 15);
        const tableColumn = ["Nombre", "Unidad", "Stock Actual", "Estado"];
        const tableRows = [];
        
        supplies.forEach(s => {
            const data = [
                s.name,
                s.unit_display,
                Math.round(s.current_stock),
                s.is_active ? 'Activo' : 'Inactivo'
            ];
            tableRows.push(data);
        });
        
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 20
        });
        
        doc.save(`Inventario_${new Date().toLocaleDateString()}.pdf`);
    };

    const exportExcel = () => {
        const tableData = supplies.map(s => ({
            Nombre: s.name,
            Unidad: s.unit_display,
            'Stock Actual': Math.round(s.current_stock),
            Estado: s.is_active ? 'Activo' : 'Inactivo'
        }));

        const worksheet = XLSX.utils.json_to_sheet(tableData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Bodega");
        
        // Generar archivo Excel y descargar
        XLSX.writeFile(workbook, `Inventario_${new Date().toLocaleDateString()}.xlsx`);
    };

    // Si el empleado no es administrador, no tiene acceso a la vista de Bodega
    if (!isAdmin) {
        return <Navigate to="/fast-food/inventory" replace />;
    }

    if (loading && supplies.length === 0) return <div>Cargando Bodega...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>Bodega e Insumos</h2>
            </div>

            <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '10px' }}>
                <button className={`btn ${activeTab === 'insumos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('insumos')}>Insumos</button>
                {isAdmin && <button className={`btn ${activeTab === 'movimientos' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('movimientos')}>Movimientos</button>}
                <button className={`btn ${activeTab === 'recetas' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('recetas')}>Recetas (Descuentos)</button>
            </div>

            {/* TAB INSUMOS */}
            {activeTab === 'insumos' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '10px' }}>
                        <button className="btn btn-secondary" onClick={handlePrintThermalStock} title="Imprimir reporte en Impresora Térmica">
                            <i className="bi bi-printer"></i> Imprimir Ticket
                        </button>
                        <button className="btn btn-success" onClick={exportExcel} title="Exportar a Excel (.xlsx)">
                            <i className="bi bi-file-earmark-excel"></i> Excel
                        </button>
                        <button className="btn btn-danger" onClick={exportPDF} title="Exportar a PDF">
                            <i className="bi bi-file-earmark-pdf"></i> PDF
                        </button>
                        <button className="btn btn-primary" onClick={() => {
                            setEditingSupply(null);
                            setSupplyForm({ name: '', description: '', unit: unitOptions.length ? unitOptions[0].value : '', current_stock: 0, cost_per_unit: '', is_active: true, is_production_item: false });
                            setIsSupplyModalOpen(true);
                        }}>Nuevo Insumo</button>
                    </div>
                    
                    {isAdmin && (() => {
                        const inventoryValue = supplies.reduce((acc, s) => acc + Math.max(0, parseFloat(s.current_stock) || 0) * (parseFloat(s.cost_per_unit) || 0), 0);
                        const withoutCost = supplies.filter(s => s.is_active && !(parseFloat(s.cost_per_unit) > 0));
                        return (
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                <div style={{ flex: '1 1 220px', padding: '12px 16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#166534', textTransform: 'uppercase' }}>Valor del inventario</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#15803d' }}>{money(inventoryValue)}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>stock actual × costo promedio de compra</div>
                                </div>
                                {withoutCost.length > 0 && (
                                    <div style={{ flex: '2 1 320px', padding: '12px 16px', borderRadius: '12px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '13px' }}>
                                        <strong><i className="bi bi-exclamation-triangle-fill"></i> {withoutCost.length} insumo(s) sin precio de compra:</strong>{' '}
                                        {withoutCost.slice(0, 6).map(s => s.name).join(', ')}{withoutCost.length > 6 ? '…' : ''}.
                                        <div>Registra un ingreso con su precio (o edítalos) para que el costo y la ganancia de las ventas salgan completos.</div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Unidad</th>
                                    {isAdmin && <th>Stock Actual</th>}
                                    {isAdmin && <th>Costo por unidad</th>}
                                    {isAdmin && <th>Valor en bodega</th>}
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supplies.length === 0 ? <tr><td colSpan="7">No hay insumos</td></tr> : supplies.map(s => (
                                    <tr key={s.id}>
                                        <td>{s.name}</td>
                                        <td>{s.unit_display}</td>
                                        {isAdmin && <td style={{ fontWeight: 'bold', color: s.current_stock <= 0 ? 'red' : 'green'}}>{formatQty(s.current_stock)}</td>}
                                        {isAdmin && (
                                            <td style={{ color: parseFloat(s.cost_per_unit) > 0 ? 'inherit' : '#b45309' }}>
                                                {parseFloat(s.cost_per_unit) > 0 ? `${unitCost(s.cost_per_unit)} / ${s.unit_display}` : 'Sin precio'}
                                            </td>
                                        )}
                                        {isAdmin && <td style={{ fontWeight: 600 }}>{money(Math.max(0, parseFloat(s.current_stock) || 0) * (parseFloat(s.cost_per_unit) || 0))}</td>}
                                        <td>
                                            {s.is_active ? 'Activo' : 'Inactivo'}
                                            {s.is_production_item && <span style={{ marginLeft: '4px', fontSize: '11px', background: '#0d6efd', color: '#fff', padding: '1px 6px', borderRadius: '8px' }}>Mezcla</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                setEditingSupply(s);
                                                setSupplyForm({ name: s.name, description: s.description, unit: s.unit, current_stock: s.current_stock, cost_per_unit: s.cost_per_unit, is_active: s.is_active, is_production_item: s.is_production_item || false });
                                                setIsSupplyModalOpen(true);
                                            }}>Editar</button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-danger" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => handleDeleteSupply(s.id)}>Eliminar</button>
                                            )}
                                            
                                            <button className="btn btn-success" style={{ marginRight: '5px', padding: '0.25rem' }} title="Agregar Stock" onClick={() => {
                                                setMovementForm({ supply: s.id, movement_type: 'in', quantity: 0, unit_cost: '', total_cost: '', reason: '', lockedSupply: true });
                                                setIsMovementModalOpen(true);
                                            }}><i className="bi bi-arrow-up"></i></button>
                                            
                                            {isAdmin && (
                                                <button className="btn btn-warning" style={{ padding: '0.25rem' }} title="Reducir Stock" onClick={() => {
                                                    setMovementForm({ supply: s.id, movement_type: 'out', quantity: 0, unit_cost: '', total_cost: '', reason: '', lockedSupply: true });
                                                    setIsMovementModalOpen(true);
                                                }}><i className="bi bi-arrow-down"></i></button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB MOVIMIENTOS */}
            {activeTab === 'movimientos' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setMovementForm({ supply: supplies[0]?.id || '', movement_type: 'in', quantity: 0, unit_cost: '', total_cost: '', reason: '', lockedSupply: false });
                            setIsMovementModalOpen(true);
                        }}>Registrar Movimiento</button>
                    </div>
                    
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Insumo</th>
                                    <th>Tipo</th>
                                    <th>Cantidad</th>
                                    <th>Costo</th>
                                    <th>Motivo / Ref</th>
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.length === 0 ? <tr><td colSpan="7">No hay movimientos</td></tr> : movements.map(m => (
                                    <tr key={m.id}>
                                        <td>{new Date(m.created_at).toLocaleString()}</td>
                                        <td>{m.supply_name}</td>
                                        <td>
                                            <span className={`tag ${m.movement_type === 'in' ? 'tag-green' : m.movement_type === 'out' ? 'tag-red' : 'tag-gray'}`}>
                                                {m.movement_type_display}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: m.movement_type === 'in' ? 'green' : (m.movement_type === 'out' || m.movement_type === 'sale' ? 'red' : 'inherit') }}>
                                            {m.movement_type === 'in' ? '+' : (m.movement_type === 'out' || m.movement_type === 'sale' ? '-' : '')}{formatQty(m.quantity)}
                                        </td>
                                        <td>
                                            {parseFloat(m.total_cost) > 0 ? (
                                                <span title={`${unitCost(m.unit_cost)} por unidad`}>
                                                    {m.movement_type === 'in' ? 'Compra ' : ''}{money(m.total_cost)}
                                                </span>
                                            ) : <span style={{ color: '#94a3b8' }}>-</span>}
                                        </td>
                                        <td>{m.reason} {m.reference_id && `(Ref: ${m.reference_id})`}</td>
                                        <td>{m.created_by}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB RECETAS */}
            {activeTab === 'recetas' && (() => {
                const productsWithRecipes = [...new Set(allRecipes.map(r => r.product))].map(productId => {
                    const recipes = allRecipes.filter(r => r.product === productId);
                    return {
                        product: productId,
                        product_name: allRecipes.find(r => r.product === productId).product_name,
                        recipes,
                        cost: recipeCost(recipes),
                        price: parseFloat(products.find(p => p.id === productId)?.price) || 0
                    }
                });

                return (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                        <button className="btn btn-primary" onClick={() => {
                            setRecipeForm({ id: null, product: products[0]?.id || '', supply: supplies[0]?.id || '', quantity: 0 });
                            setIsRecipeModalOpen(true);
                        }}>Nueva Receta</button>
                    </div>

                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Producto Principal</th>
                                    <th>Insumos Configurados</th>
                                    <th>Costo de insumos</th>
                                    <th>Precio de venta</th>
                                    <th>Ganancia por unidad</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productsWithRecipes.length === 0 ? <tr><td colSpan="6">No hay productos con recetas configuradas.</td></tr> : productsWithRecipes.map(p => (
                                    <tr key={p.product}>
                                        <td>{p.product_name}</td>
                                        <td>{p.recipes.length} insumo(s)</td>
                                        <td>{money(p.cost)}</td>
                                        <td>{money(p.price)}</td>
                                        <td style={{ fontWeight: 700, color: p.price - p.cost >= 0 ? '#15803d' : '#dc2626' }}>
                                            {money(p.price - p.cost)}{p.price > 0 && ` (${(((p.price - p.cost) / p.price) * 100).toFixed(0)}%)`}
                                        </td>
                                        <td>
                                            <button className="btn btn-primary" style={{ padding: '0.25rem' }} onClick={() => setViewDetailsProductId(p.product)}>Ver Detalles / Editar</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                );
            })()}

            {/* FIN TABLAS - Mezclas movido a su propia página /fast-food/mezclas */}

            {/* MODALS */}
            {/* Modal Insumo */}
            <Modal isOpen={isSupplyModalOpen} onClose={() => setIsSupplyModalOpen(false)} title={editingSupply ? 'Editar Insumo' : 'Nuevo Insumo'}>
                <form onSubmit={handleSupplySubmit}>
                    <div className="form-group">
                        <label>Nombre del Insumo</label>
                        <input type="text" required value={supplyForm.name} onChange={e => setSupplyForm({...supplyForm, name: e.target.value})} />
                    </div>
                    <div className="form-group">
                        <label>Unidad de Medida</label>
                        <select className="form-control" value={supplyForm.unit} onChange={e => setSupplyForm({...supplyForm, unit: e.target.value})}>
                            {unitOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.display_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Stock Inicial</label>
                        <input type="number" step="any" min="0" required value={supplyForm.current_stock} onChange={e => setSupplyForm({...supplyForm, current_stock: e.target.value})} disabled={!!editingSupply} />
                        {editingSupply && <small>Para modificar el stock de un insumo existente, registre un movimiento (Ingreso/Ajuste).</small>}
                    </div>
                    <div className="form-group">
                        <label>{editingSupply ? 'Costo por unidad (costo promedio)' : 'Precio de compra por unidad'} ($)</label>
                        <input type="number" step="any" min="0" value={supplyForm.cost_per_unit} placeholder="Ej: 0.35"
                            onChange={e => setSupplyForm({...supplyForm, cost_per_unit: e.target.value})} />
                        {!editingSupply && parseFloat(supplyForm.current_stock) > 0 && parseFloat(supplyForm.cost_per_unit) > 0 && (
                            <small>Compra inicial: {money(parseFloat(supplyForm.current_stock) * parseFloat(supplyForm.cost_per_unit))} (se registra como gasto en bodega)</small>
                        )}
                        {editingSupply && <small>Se actualiza solo con cada ingreso que tenga precio de compra. Cámbialo aquí solo para corregirlo.</small>}
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" id="is_production_item"
                            checked={supplyForm.is_production_item}
                            onChange={e => setSupplyForm({...supplyForm, is_production_item: e.target.checked})} />
                        <label htmlFor="is_production_item" style={{ margin: 0 }}>
                            Producto de mezcla (no aparece en POS, solo se gestiona desde Mezclas)
                        </label>
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsSupplyModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Movimiento */}
            <Modal isOpen={isMovementModalOpen} onClose={() => setIsMovementModalOpen(false)} title="Registrar Movimiento de Bodega">
                <form onSubmit={handleMovementSubmit}>
                    <div className="form-group">
                        <label>Insumo</label>
                        <select className="form-control" required value={movementForm.supply} onChange={e => setMovementForm({...movementForm, supply: e.target.value})} disabled={movementForm.lockedSupply}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} {isAdmin ? `(Stock actual: ${s.current_stock})` : ''}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Tipo de Movimiento</label>
                        <select className="form-control" value={movementForm.movement_type} onChange={e => setMovementForm({...movementForm, movement_type: e.target.value})}>
                            <option value="in">Ingreso (Sumar al stock)</option>
                            {isAdmin && (
                                <>
                                    <option value="out">Egreso (Restar al stock)</option>
                                    <option value="adjustment">Ajuste (Reemplazar stock exacto)</option>
                                </>
                            )}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cantidad</label>
                        <input type="number" step="any" min="0" required value={movementForm.quantity} onChange={e => setMovementForm({...movementForm, quantity: e.target.value})} />
                    </div>
                    {movementForm.movement_type === 'in' && (() => {
                        const qty = parseFloat(movementForm.quantity) || 0;
                        const setUnit = (v) => setMovementForm({ ...movementForm, unit_cost: v, total_cost: v !== '' && qty > 0 ? (parseFloat(v) * qty).toFixed(2) : '' });
                        const setTotal = (v) => setMovementForm({ ...movementForm, total_cost: v, unit_cost: v !== '' && qty > 0 ? (parseFloat(v) / qty).toFixed(4) : '' });
                        return (
                            <div className="form-group" style={{ padding: '10px', borderRadius: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <label style={{ fontWeight: 700 }}>Precio de compra</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div>
                                        <small>Por unidad ($)</small>
                                        <input type="number" step="any" min="0" value={movementForm.unit_cost} placeholder="Ej: 0.30" onChange={e => setUnit(e.target.value)} />
                                    </div>
                                    <div>
                                        <small>Total pagado ($)</small>
                                        <input type="number" step="any" min="0" value={movementForm.total_cost} placeholder="Ej: 15.00" onChange={e => setTotal(e.target.value)} />
                                    </div>
                                </div>
                                <small>Llena uno de los dos. Se registra como gasto en compras de bodega y actualiza el costo promedio del insumo.</small>
                            </div>
                        );
                    })()}
                    <div className="form-group">
                        <label>Motivo</label>
                        <input type="text" required value={movementForm.reason} onChange={e => setMovementForm({...movementForm, reason: e.target.value})} placeholder="Ej: Compra a proveedor, daño..." />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsMovementModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Registrar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Detalles Receta */}
            {viewDetailsProductId && (() => {
                const viewDetailsProduct = [...new Set(allRecipes.map(r => r.product))]
                    .map(productId => ({
                        product: productId,
                        product_name: allRecipes.find(r => r.product === productId).product_name,
                        recipes: allRecipes.filter(r => r.product === productId)
                    })).find(p => p.product === viewDetailsProductId);

                if (!viewDetailsProduct) return null;

                return (
                    <Modal isOpen={true} onClose={() => setViewDetailsProductId(null)} title={`Detalles de Receta: ${viewDetailsProduct.product_name}`} maxWidth="800px">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                            <button className="btn btn-primary" onClick={() => {
                                setRecipeForm({ id: null, product: viewDetailsProduct.product, supply: supplies[0]?.id || '', quantity: 0 });
                                setIsRecipeModalOpen(true);
                            }}>Agregar Insumo a esta Receta</button>
                        </div>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Insumo</th>
                                        <th>Cantidad</th>
                                        <th>Costo</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewDetailsProduct.recipes.map(r => (
                                        <tr key={r.id}>
                                            <td>{r.supply_name}</td>
                                            <td>{formatQty(r.quantity)} {r.supply_unit}</td>
                                            <td>{money(recipeCost([r]))}</td>
                                            <td>
                                                <button className="btn btn-secondary" style={{ marginRight: '5px', padding: '0.25rem' }} onClick={() => {
                                                    setRecipeForm({ id: r.id, product: r.product, supply: r.supply, quantity: r.quantity });
                                                    setIsRecipeModalOpen(true);
                                                }}>Editar</button>
                                                <button className="btn btn-danger" style={{ padding: '0.25rem' }} onClick={() => handleDeleteRecipe(r.id)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(() => {
                            const cost = recipeCost(viewDetailsProduct.recipes);
                            const price = parseFloat(products.find(p => p.id === viewDetailsProduct.product)?.price) || 0;
                            return (
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px', padding: '10px 14px', borderRadius: '10px', background: '#f8fafc', fontSize: '14px' }}>
                                    <span>Costo de insumos: <strong>{money(cost)}</strong></span>
                                    <span>Precio de venta: <strong>{money(price)}</strong></span>
                                    <span style={{ color: price - cost >= 0 ? '#15803d' : '#dc2626' }}>
                                        Ganancia por unidad: <strong>{money(price - cost)}</strong>{price > 0 && ` (${(((price - cost) / price) * 100).toFixed(0)}%)`}
                                    </span>
                                </div>
                            );
                        })()}
                    </Modal>
                );
            })()}

            {/* Modal Receta */}
            <Modal isOpen={isRecipeModalOpen} onClose={() => setIsRecipeModalOpen(false)} title={recipeForm.id ? "Editar Receta" : "Nueva Receta"}>
                <form onSubmit={handleRecipeSubmit}>
                    <div className="form-group">
                        <label>Producto del Menú</label>
                        <select className="form-control" required value={recipeForm.product} onChange={e => setRecipeForm({...recipeForm, product: e.target.value})}>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Insumo a descontar</label>
                        <select className="form-control" required value={recipeForm.supply} onChange={e => setRecipeForm({...recipeForm, supply: e.target.value})}>
                            {supplies.map(s => <option key={s.id} value={s.id}>{s.name} ({s.unit_display})</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Cantidad a descontar por cada venta</label>
                        <input type="number" step="any" min="0" required value={recipeForm.quantity} onChange={e => setRecipeForm({...recipeForm, quantity: e.target.value})} />
                    </div>
                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsRecipeModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* Modal Confirmación de Impresión */}
            <Modal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
                title={printModal.success ? "Estado de Impresión" : "Atención"}
                maxWidth="420px"
            >
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{
                        fontSize: '3rem',
                        color: printModal.success ? 'var(--success-color, #10b981)' : 'var(--danger-color, #ef4444)',
                        marginBottom: '1rem'
                    }}>
                        <i className={printModal.success ? "bi bi-check-circle-fill" : "bi bi-exclamation-triangle-fill"}></i>
                    </div>
                    <h4 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                        {printModal.success ? "¡Impresión Solicitada!" : "Error al Imprimir"}
                    </h4>
                    <p style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                        {printModal.message}
                    </p>
                    <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
                            style={{ minWidth: '120px' }}
                        >
                            Aceptar
                        </button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default Bodega;
